import type { StationsResponse, PriceHistoryResponse, ApiError, SearchLocation } from '@/types';
import { API_URL } from '@/constants';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async fetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    // Check for stale data header
    const isStale = response.headers.get('X-Data-Stale') === 'true';
    
    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'unknown_error',
        message: `Request failed with status ${response.status}`,
      }));
      
      throw new ApiClientError(
        error.message || error.error,
        response.status,
        error.error
      );
    }

    const data = await response.json();
    
    // Attach stale flag to response if needed
    if (isStale && typeof data === 'object' && data !== null) {
      (data as Record<string, unknown>)._isStale = true;
    }
    
    return data as T;
  }

  /**
   * Fetch stations within a radius of a location
   */
  async getStations(
    location: SearchLocation,
    radius: number
  ): Promise<StationsResponse & { _isStale?: boolean }> {
    const params = new URLSearchParams({
      lat: location.latitude.toString(),
      lng: location.longitude.toString(),
      radius: radius.toString(),
    });
    
    return this.fetch<StationsResponse>(`/stations?${params}`);
  }

  /**
   * Fetch price history for a station
   */
  async getStationHistory(stationId: string): Promise<PriceHistoryResponse> {
    return this.fetch<PriceHistoryResponse>(`/station/${stationId}/history`);
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.fetch<{ status: string; timestamp: string }>('/health');
  }
}

export class ApiClientError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_URL);

// Export type for use in hooks
export type { ApiClient };
